/* eslint-disable max-len */
import React, { useEffect } from 'react';
import proptypes from 'prop-types';
const { func, shape, string, arrayOf, bool, number } = proptypes;
import { extend } from '@thd-nucleus/data-sources';
import { useImpression } from '@thd-olt-component-react/impression';
import {
  Card,
  CardBody,
  CardTitle,
  Link,
  Typography,
  Badge
} from '@one-thd/sui-atomic-components';
import { MoreButton } from '@thd-olt-component-react/project-forms';
import { Apron } from '@one-thd/sui-icons';
import { Thumbnails } from './Thumbnails';
import { ProjectTilePlaceholder } from './ProjectTilePlaceholder';
import { PROJECT_STATUS_ACTIVE, PROJECT_STATUS_ARCHIVED } from '../project-summary-helper';
import { dateModifiedMessage } from '../../helpers';

// Helper functions extracted to reduce cognitive complexity
const getStatusInfo = (projectStatus) => {
  const isActive = projectStatus === PROJECT_STATUS_ACTIVE;
  return {
    text: isActive ? PROJECT_STATUS_ACTIVE : PROJECT_STATUS_ARCHIVED,
    color: isActive ? 'info' : 'medium',
    textColor: isActive ? 'primary' : 'subtle'
  };
};

const renderProjectHeader = (projectName, url, onClick, statusInfo) => {

  return (
    <a href={url} onClick={() => onClick()}>
      <Typography
        variant="h3"
        weight="bold"
        color={statusInfo.textColor}
        className="sui-break-all"
      >
        {projectName}
      </Typography>
    </a>
  );
};

const renderCreatedBySection = (project, textColor) => {
  if (project.isCreatedByTHDAssociate) {
    return (
      <div className="sui-flex sui-items-baseline">
        <Typography variant="body-xs" color={textColor}>
          Created By:
        </Typography>
        <div className="sui-mr-2 sui-ml-2">
          <Apron color="brand" size="small" />
        </div>
        <Typography variant="body-xs" color={textColor}>
          THD Associate
        </Typography>
      </div>
    );
  }

  return (
    <Typography variant="body-xs" color={textColor}>
      Created By: {project?.createdByName}
    </Typography>
  );
};

export const ProjectTile = ({
  projectId, project, position, loading, disableAction, onClick
}) => {

  useEffect(() => {
    LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('project-tile.ready');
  }, []);

  const projectName = project?.projectName ?? '';
  const url = `/projects/${project?.id}`;
  const itemCount = project?.numberOfItems || 0;
  const itemCountMessage = `${itemCount} ${itemCount === 1 ? ' item' : ' items'}`;

  // Get status information
  const statusInfo = getStatusInfo(project.status);

  // Set up impression tracking
  const impressionContextData = useImpression({
    data: {
      id: projectId,
      name: 'ProjectTile',
      component: 'ProjectTile',
      position: position + 1,
      type: 'content'
    }
  });

  const projectTileRef = impressionContextData.ref;
  const projectTileClickId = impressionContextData.clickID;

  if (!project && loading) return <ProjectTilePlaceholder />;
  if (!project) return null;

  // Render the project tile
  return (
    // eslint-disable-next-line react/no-unknown-property
    <div className="sui-w-full" ref={projectTileRef} clickid={projectTileClickId} data-component="ProjectTile" data-testid="project-tile">
      <Card className="sui-grid sui-rounded-md sui-shadow-base sui-h-full sui-my-0">
        <CardBody>
          <div className="sui-flex sui-mb-2">
            <Badge data-testid="badge-status" size="small" variant="subtle" color={statusInfo.color}>
              <span className="sui-capitalize">
                {statusInfo.text}
              </span>
            </Badge>
          </div>
          <CardTitle
            header={renderProjectHeader(projectName, url, onClick, statusInfo)}
            subheader={<Link href={url} onClick={() => onClick()} className="sui-font-regular sui-text-base">Project Number: {project?.projectId}</Link>}
            action={!disableAction && (
              <MoreButton
                projectStatus={project.status}
                projectName={projectName}
                projectId={projectId}
                isProHouseProject={project?.hasTradeCreditAttached ?? false}
              />
            )}
            disableActionSpacing
          />
        </CardBody>
        <CardBody>
          <Thumbnails url={url} thumbnails={project?.images} />
        </CardBody>
        <CardBody>
          {renderCreatedBySection(project, statusInfo.textColor)}
          <Typography variant="body-xs" color={statusInfo.textColor}>{itemCountMessage}</Typography>
          <Typography variant="body-xs" color="subtle">Last Modified: {dateModifiedMessage(project?.modifyDateTime ?? '')}</Typography>
        </CardBody>
      </Card>
    </div>
  );
};

ProjectTile.displayName = 'ProjectTile';

ProjectTile.propTypes = {
  projectId: string.isRequired,
  project: shape({
    projectId: string,
    projectName: string,
    status: string,
    modifyDateTime: string,
    createdBy: string,
    createdByName: string,
    isCreatedByTHDAssociate: bool,
    numberOfItems: number,
    images: arrayOf(string)
  }),
  position: number,
  loading: bool,
  disableAction: bool,
  onClick: func
};

ProjectTile.defaultProps = {
  project: null,
  position: 0,
  loading: true,
  disableAction: false,
  onClick: () => {}
};

ProjectTile.dataModel = extend(
  MoreButton
);
